/**
 * Safelist for HTML embedded in an assistant response.
 *
 * ## Why this is a security boundary, not a styling preference
 *
 * The assistant's output is **attacker-influenceable**. Order notes, buyer
 * messages, product titles, publication descriptions and channel payloads all
 * flow into the prompt as tool results, so a seller's own catalogue — or a
 * buyer typing into a marketplace message box — can steer what the model writes
 * back. Treat the response exactly like user input from a stranger.
 *
 * The chat renders that output through `rehype-raw` (so `<b>`, `<br>`, a small
 * table written as HTML actually render) which, on its own, is a textbook HTML
 * injection sink. This schema is the thing standing between the two. Widening
 * it is a security decision: a new tag or attribute here must be justified
 * against "what can a hostile string do with it", not against "the model
 * emitted one once".
 *
 * ## What it does NOT rely on
 *
 * `hast-util-sanitize`'s `defaultSchema` is already an allowlist and already
 * excludes every dangerous tag. We do not merely trust that: the forbidden set
 * below is subtracted from `tagNames` explicitly and added to `strip`, the URL
 * protocols are re-stated, and every `on*` attribute is filtered out of every
 * attribute list. All of those are no-ops against today's `defaultSchema` — they
 * exist so a dependency bump that loosens the default cannot loosen us, and
 * `MarkdownMessage.test.tsx` asserts each of them.
 *
 * There is deliberately **no `dangerouslySetInnerHTML`** anywhere in this path.
 * Both `rehype-raw` and `rehype-sanitize` operate on the hast AST; React still
 * creates every node. See `WidgetLayout/Widgets/WebWidget/TextWidget.tsx` for
 * the house rule.
 */

import { defaultSchema } from 'rehype-sanitize';
import type { Options as SanitizeSchema } from 'rehype-sanitize';

type AttributeValue = NonNullable<SanitizeSchema['attributes']>[string][number];

/**
 * Tags that must never survive, with their children discarded rather than
 * unwrapped. `script` / `style` carry code; `iframe` / `object` / `embed` /
 * `portal` / `frame` embed a foreign document; `form` / `input`-adjacent
 * controls phish; `svg` / `math` are foreign-content parsing shortcuts that
 * have historically been used to smuggle script.
 *
 * `input` is NOT here: GFM task lists emit one, and `defaultSchema.required`
 * pins it to `type="checkbox" disabled`.
 */
const FORBIDDEN_TAG_NAMES = [
  'applet',
  'audio',
  'base',
  'button',
  'canvas',
  'embed',
  'form',
  'frame',
  'frameset',
  'iframe',
  'link',
  'math',
  'meta',
  'noscript',
  'object',
  'option',
  'portal',
  'script',
  'select',
  'style',
  'svg',
  'template',
  'textarea',
  'video'
];

/** No `javascript:`, no `data:`, no `vbscript:` — an allowlist, not a denylist. */
const LINK_PROTOCOLS = [ 'http', 'https', 'mailto' ];
const MEDIA_PROTOCOLS = [ 'http', 'https' ];

const attributeName = ( attribute: AttributeValue ): string =>
  ( Array.isArray( attribute ) ? String( attribute[0]) : String( attribute ));

/**
 * `on*` never reaches the DOM. `defaultSchema` allows none today; this makes
 * that a property of OUR schema rather than of the dependency's.
 */
const isEventHandler = ( attribute: AttributeValue ): boolean =>
  /^on[a-z]/i.test( attributeName( attribute ));

const withoutEventHandlers = (
  attributes: SanitizeSchema['attributes']
): SanitizeSchema['attributes'] =>
  Object.fromEntries(
    Object.entries( attributes ?? {}).map(([ tag, list ]) => [
      tag,
      ( list ?? []).filter( attribute => !isEventHandler( attribute ))
    ])
  );

/** `class="language-ts"` and nothing else — see the note on the schema below. */
const LANGUAGE_CLASS_NAME: AttributeValue = [ 'className', /^language-./ ];

/**
 * The chat's safelist: `defaultSchema`, minus the forbidden set, plus the one
 * thing markdown code fences actually need.
 *
 * `defaultSchema` already allows `className` on `code` restricted to
 * `/^language-./`, which is where react-markdown puts the fence's language
 * hint. `pre` gets the same value-restricted allowance so an assistant that
 * writes the class on the wrapper instead does not lose it. Both are pinned to
 * the `language-` prefix — an unrestricted `className` would let injected
 * markup borrow app styles and impersonate real UI.
 */
export const markdownSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: ( defaultSchema.tagNames ?? []).filter(
    tag => !FORBIDDEN_TAG_NAMES.includes( tag )
  ),
  strip: [ ...new Set([ ...( defaultSchema.strip ?? []), ...FORBIDDEN_TAG_NAMES ]) ],
  protocols: {
    ...defaultSchema.protocols,
    href: LINK_PROTOCOLS,
    cite: LINK_PROTOCOLS,
    src: MEDIA_PROTOCOLS,
    srcSet: MEDIA_PROTOCOLS,
    longDesc: MEDIA_PROTOCOLS
  },
  attributes: {
    ...withoutEventHandlers( defaultSchema.attributes ),
    pre: [
      ...(( defaultSchema.attributes?.pre ?? []) as AttributeValue[]),
      LANGUAGE_CLASS_NAME
    ]
  }
};

/** Exported for the test that asserts the forbidden set is actually subtracted. */
export const FORBIDDEN_MARKDOWN_TAG_NAMES = FORBIDDEN_TAG_NAMES;
